import { useEffect, useMemo, useRef, useState } from "react";
import InfoTip from "@/shared/components/infoTip/InfoTip";
import TouchPreview, { type PreviewFrame } from "./TouchPreview";
import { useAdTokens } from "@/shared/adTokens/useAdTokens";
import CopyField from "./CopyField";
import {
  bodyWithCta,
  ctaFromBody,
  htmlFromParagraphs,
  paragraphsFromHtml,
  lookupToken,
  smsCountFor,
  tokensIn,
  vocabularyOf,
} from "./touchCopy";
import { anchorName, sendDate, type KeyDateRef, type Touch } from "./emailSmsTypes";

export type EditorScope = "campaign" | "catalog";

/*
    Preview reads; Edit copy writes. Two modes rather than one always-editable screen because
    the editor is opened to look at a touch at least as often as to change it, and a form
    that is live the moment it opens invites an accidental keystroke into copy a client has
    already approved.
*/
export type EditorMode = "preview" | "edit";

type Props = {
  touch: Touch;
  /** Every touch in the sequence, so the header can step through them in order. */
  siblings: Touch[];
  keyDates: KeyDateRef[];
  /** Breadcrumb: "Applicant Playbook › SP27 Applicants" or the catalog equivalent. */
  crumb: string;
  scope: EditorScope;
  /** Which mode to open in. The row menu's Preview and Edit copy differ only by this. */
  initialMode?: EditorMode;
  values: Record<string, string>;
  fromName: string;
  fromAddress: string;
  smsSender: string;
  footerAddress: string;
  /** The address a test will come from. Absent in the Catalog, which has no Send test. */
  testFrom?: string;
  /** Set when the catalog original moved on after this campaign customized it. */
  catalogUpdated?: boolean;
  onSave: (next: Partial<Touch>) => void;
  onReset?: () => void;
  /*
      Absent where there is nothing to send. A catalog message belongs to no campaign, so it
      has no client, no sending account and no token values to render against — the v10
      prototype gives the catalog's message detail only Open, Edit design and Publish, and
      no Send test at all. This was a required prop, so the catalog passed a no-op to satisfy
      it: the button rendered, took an address and discarded it.
  */
  onSendTest?: (to: string) => void;
  onStep: (touchId: string) => void;
  onClose: () => void;
};

type Draft = {
  title: string;
  subject: string;
  pre: string;
  body: string;
  cta: string;
  text: string;
};

const draftOf = (touch: Touch): Draft => ({
  title: touch.title,
  subject: touch.subject ?? "",
  pre: touch.pre ?? "",
  body: touch.body ?? "",
  cta: touch.cta ?? "",
  text: touch.text ?? "",
});

export function TouchEditor({
  touch,
  siblings,
  keyDates,
  crumb,
  scope,
  initialMode = "preview",
  values,
  fromName,
  fromAddress,
  smsSender,
  footerAddress,
  testFrom,
  catalogUpdated,
  onSave,
  onReset,
  onSendTest,
  onStep,
  onClose,
}: Props) {
  const isEmail = touch.ch === "email";
  const [draft, setDraft] = useState<Draft>(() => draftOf(touch));
  const [mode, setMode] = useState<EditorMode>(initialMode);
  const readOnly = mode === "preview";
  const [frame, setFrame] = useState<PreviewFrame>("desktop");
  const [theme, setTheme] = useState<"light" | "dark">("light");
  /* Off per open. Advanced is an escape hatch, not a preference: leaving it on would hand
     the next person who opens any message a box of markup. */
  const [advanced, setAdvanced] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState("");

  /*
      Stepping to another touch replaces the whole draft; an edit in progress on the previous
      one is not carried across into different copy.

      Skipped on the first run, which is the fix: an effect fires on mount, so this used to
      overwrite `initialMode` the moment the editor opened and every "Edit" that asked for
      the form landed on the preview instead. Only a STEP should reset the mode.
  */
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    setDraft(draftOf(touch));
    setAdvanced(false);
    // Back to reading on the next touch. Carrying edit mode across the stepper would leave
    // a live form open on copy somebody only meant to look at.
    setMode("preview");
  }, [touch]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const index = siblings.findIndex((t) => t.id === touch.id);
  const when = sendDate(touch, keyDates);

  const dirty = useMemo(() => {
    const original = draftOf(touch);
    return (Object.keys(original) as (keyof Draft)[]).some((key) => original[key] !== draft[key]);
  }, [draft, touch]);

  const preview: Touch = {
    ...touch,
    title: draft.title,
    subject: draft.subject,
    pre: draft.pre,
    body: draft.body,
    cta: draft.cta,
    text: draft.text,
  };

  const { tokens: adTokens } = useAdTokens();
  const vocabulary = useMemo(() => vocabularyOf(adTokens), [adTokens]);

  const smsCount = smsCountFor(draft.text, values, vocabulary);
  const preLength = draft.pre.length;
  const ctaMissing = isEmail && draft.cta.trim() === "";

  const copy = isEmail ? `${draft.subject} ${draft.pre} ${draft.body}` : draft.text;
  const used = tokensIn(copy);
  const emptyTokens = used.filter(
    (key) => vocabulary.known(key) && !lookupToken(key, values, vocabulary),
  );

  const warnings: string[] = [];
  if (!isEmail && smsCount.seg > 1) {
    warnings.push(
      `This text is ${smsCount.seg} segments (${smsCount.enc}), so it bills ${smsCount.seg}× per recipient.`,
    );
  }

  const setField = (key: keyof Draft, value: string) =>
    setDraft((prev) => {
      if (key === "cta") return { ...prev, cta: value, body: bodyWithCta(prev.body, value) };
      if (key === "body") return { ...prev, body: value, ...ctaFromBody(value) };
      return { ...prev, [key]: value };
    });

  const save = () => {
    onSave({
      title: draft.title,
      subject: draft.subject,
      pre: draft.pre,
      body: draft.body,
      cta: draft.cta,
      text: draft.text,
      customized: scope === "campaign" ? true : touch.customized,
    });
  };

  return (
    <div className="tx" role="dialog" aria-modal="true" aria-label={touch.title}>
      <div className="tx__head">
        <span className="tx__accent" />
        <div className="tx__ident">
          <div className="tx__ident-line">
            <span className="tx__title">{draft.title}</span>
            <span className={`touch-ch touch-ch--${touch.ch}`}>
              {isEmail ? "Email" : "SMS"}
            </span>
            <span className={`touch-pill touch-pill--${touch.on === false ? "off" : "on"}`}>
              {touch.on === false ? "Off" : "On"}
            </span>
          </div>
          <div className="tx__crumb">{crumb}</div>
        </div>

        <div className="tx__stepper">
          <button
            type="button"
            className="esms-seq__chev"
            aria-label="Previous touch"
            disabled={index <= 0}
            onClick={() => onStep(siblings[index - 1].id)}
          >
            <i className="bi bi-chevron-left" />
          </button>
          <div className="tx__step">
            <span className="tx__step-n">
              Step {index + 1} of {siblings.length}
            </span>
            <span className="tx__step-date">
              {when
                ? when.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : `${touch.dir === "on" ? "On" : `${touch.days} days ${touch.dir}`} ${anchorName(touch.anchor)}`}
            </span>
          </div>
          <button
            type="button"
            className="esms-seq__chev"
            aria-label="Next touch"
            disabled={index < 0 || index >= siblings.length - 1}
            onClick={() => onStep(siblings[index + 1].id)}
          >
            <i className="bi bi-chevron-right" />
          </button>
        </div>

        <div className="btn-group btn-group-sm" role="group" aria-label="Preview or edit copy">
          {(["preview", "edit"] as EditorMode[]).map((option) => (
            <button
              key={option}
              type="button"
              className={`btn btn-sm ${mode === option ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setMode(option)}
            >
              {option === "preview" ? "Preview" : "Edit copy"}
            </button>
          ))}
        </div>

        <button type="button" className="esms-seq__chev" aria-label="Close" onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      </div>

      <div className="tx__main">
        <div className="tx__form">
          <div className="tx__lock">
            <i className="bi bi-lock" />
            <span>
              {scope === "campaign"
                ? "Layout, colours and the tracked link come from the catalog design. Copy you change here applies to this campaign only."
                : "Edits here apply to every campaign using this sequence."}
            </span>
          </div>

          {catalogUpdated && (
            <div className="tx__updated">
              <span className="touch-pill touch-pill--blocked">! Catalog updated</span>
              <span className="tx__sep">|</span>
              <button type="button" className="esms__link">
                View changes
              </button>
            </div>
          )}

          <div>
            <label className="tx__label" htmlFor="tx-title">
              Step title
            </label>
            <input
              id="tx-title"
              className="tx__field"
              readOnly={readOnly}
              value={draft.title}
              placeholder="e.g. Registration reminder"
              onChange={(e) => setField("title", e.target.value)}
            />
          </div>

          {isEmail ? (
            <>
              <div>
                <label className="tx__label" htmlFor="tx-subject">
                  Subject
                </label>
                <CopyField
                  id="tx-subject"
                  singleLine
                  readOnly={readOnly}
                  tokens={adTokens}
                  value={draft.subject}
                  onValueChange={(v) => setField("subject", v)}
                />
              </div>

              <div>
                <label className="tx__label" htmlFor="tx-pre">
                  Preheader
                </label>
                <CopyField
                  id="tx-pre"
                  className="tx__field tx__field--pre"
                  rows={2}
                  readOnly={readOnly}
                  tokens={adTokens}
                  value={draft.pre}
                  onValueChange={(v) => setField("pre", v)}
                />
                <div
                  className={`tx__count${preLength > 90 || (preLength > 0 && preLength < 40) ? " tx__count--warn" : ""}`}
                >
                  {preLength} characters. Aim for 40 to 90.
                </div>
              </div>

              {/*
                  Plain paragraphs by default, raw HTML behind Advanced (MPN-2201).

                  This box used to show the stored HTML, tags and all, while its own hint
                  said "Plain paragraphs" -- so an Ops author editing a sentence was handed
                  markup to not break. The button anchor is deliberately absent from the
                  plain view: it has its own Button label field above, and two places to
                  edit one string is how they come to disagree.
              */}
              <div>
                <div className="tx__label tx__label--row">
                  <label htmlFor={advanced ? "tx-html" : "tx-body"}>Body copy</label>
                  <InfoTip
                    label="Body copy"
                    text={
                      advanced
                        ? "The stored HTML, including the button. Edit with care — the button's link is the campaign's tracked token."
                        : "Plain paragraphs. Leave a blank line between paragraphs. Formatting comes from the catalog design."
                    }
                  />
                  <span className="esms__spacer" />
                  <button
                    type="button"
                    className="tx__advanced"
                    aria-pressed={advanced}
                    onClick={() => setAdvanced((on) => !on)}
                  >
                    {advanced ? "Done" : "Advanced"}
                  </button>
                </div>
                {advanced ? (
                  /* The picker works here too. Advanced only swaps paragraphs for the
                     stored markup -- the copy in it still merges the same fields, and
                     dropping to raw HTML was losing the `[` menu and the highlighting. */
                  <CopyField
                    id="tx-html"
                    className="tx__field tx__field--body tx__field--code"
                    rows={8}
                    readOnly={readOnly}
                    tokens={adTokens}
                    value={draft.body}
                    onValueChange={(v) => setField("body", v)}
                  />
                ) : (
                  <CopyField
                    id="tx-body"
                    className="tx__field tx__field--body"
                    rows={6}
                    readOnly={readOnly}
                    tokens={adTokens}
                    value={paragraphsFromHtml(draft.body)}
                    onValueChange={(v) => setField("body", htmlFromParagraphs(v, draft.body))}
                  />
                )}
              </div>

              <div>
                <div className="tx__label tx__label--row">
                  <label htmlFor="tx-cta">Button label</label>
                  <InfoTip
                    label="Button label"
                    text="Text only. Button style and the tracked [link] stay locked to the catalog design."
                  />
                </div>
                <CopyField
                  id="tx-cta"
                  singleLine
                  readOnly={readOnly}
                  tokens={adTokens}
                  value={draft.cta}
                  placeholder="e.g. Finish My Application"
                  aria-invalid={ctaMissing}
                  onValueChange={(v) => setField("cta", v)}
                />
                {ctaMissing && <div className="tx__required">Required</div>}
              </div>
            </>
          ) : (
            <div>
              <label className="tx__label" htmlFor="tx-text">
                Message text
              </label>
              <CopyField
                id="tx-text"
                className="tx__field tx__field--sms"
                rows={4}
                readOnly={readOnly}
                tokens={adTokens}
                value={draft.text}
                onValueChange={(v) => setField("text", v)}
              />
              <div className="tx__stop">
                <i className="bi bi-lock" />
                <span>Reply STOP to opt out</span>
                <span className="touch-pill touch-pill--off">Added by platform</span>
              </div>
              <div className={`tx__count${smsCount.seg > 1 ? " tx__count--warn" : ""}`}>
                {smsCount.n} of 160 characters with the opt-out line · {smsCount.seg}{" "}
                {smsCount.seg === 1 ? "segment" : "segments"} ({smsCount.enc})
              </div>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="tx__warnings">
              {warnings.map((text) => (
                <div className="tx__warn" key={text}>
                  <span>!</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          )}

          {!readOnly && <div className="tx__rule" />}

          {!readOnly && (
          <div>
            <div className="tx__section">Merge fields</div>
            {/* The notes sit on each field, as they do on an ad's copy form -- a fix has to
                know which field's text it is rewriting. */}
            <div className="tx__hint">
              Type <code>[</code> in any copy field to insert a merge field.
            </div>
          </div>
          )}

          {used.length > 0 && (
            <div className="tx__vals">
              <div className="tx__section">{scope === "catalog" ? "Sample values" : "Sample values for this campaign"}</div>
              <dl className="tx__val-grid">
                {Array.from(new Set(used)).map((key) => (
                  <div className="tx__val-row" key={key}>
                    <dt>{vocabulary.canonical(key)}</dt>
                    <dd className={lookupToken(key, values, vocabulary) ? undefined : "tx__val--empty"}>
                      {lookupToken(key, values, vocabulary) || (scope === "catalog" ? "no sample value" : "no value for this campaign")}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {emptyTokens.length > 0 && (
            <div className="tx__warn">
              <span>!</span>
              <span>
                {emptyTokens.map((k) => `[${k}]`).join(", ")} has no value on this campaign, so it
                will send as an empty space.
              </span>
            </div>
          )}
        </div>

        <div className="tx__preview">
          {isEmail && (
            <div className="tx__preview-controls">
              <div className="btn-group btn-group-sm" role="group" aria-label="Preview width">
                {(["desktop", "mobile"] as PreviewFrame[]).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`btn btn-sm ${frame === option ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setFrame(option)}
                  >
                    {option === "desktop" ? "Desktop" : "Mobile"}
                  </button>
                ))}
              </div>
              <div className="btn-group btn-group-sm" role="group" aria-label="Preview theme">
                {(["light", "dark"] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`btn btn-sm ${theme === option ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => setTheme(option)}
                  >
                    {option === "light" ? "Light" : "Dark"}
                  </button>
                ))}
              </div>
            </div>
          )}

          <TouchPreview
            touch={preview}
            values={values}
            vocabulary={vocabulary}
            fromName={fromName}
            fromAddress={fromAddress}
            smsSender={smsSender}
            footerAddress={footerAddress}
            frame={frame}
            theme={theme}
          />

          <div className="tx__sample-note">
            Sample values stand in for merge fields. The real send uses each recipient's own.
          </div>
        </div>
      </div>

      <div className="tx__foot">
        <span className="tx__foot-note">
          {readOnly
            ? "Reading only. Switch to Edit copy to change anything."
            : scope === "campaign"
              ? "Saving marks this touch customized for this campaign. The catalog original is untouched."
              : "Saving publishes to every campaign using this sequence."}
        </span>

        {dirty && (
          <span className="tx__dirty">
            <span className="tx__dirty-dot" />
            Unsaved changes
          </span>
        )}

        {touch.customized && onReset && (
          <button type="button" className="esms__link" onClick={onReset}>
            Reset to catalog default
          </button>
        )}

        <span className="tx__test-wrap">
          {onSendTest && (
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={() => setTestOpen((v) => !v)}
          >
            Send test
          </button>
          )}
          {onSendTest && testOpen && (
            <div className="tx__test" role="dialog" aria-label="Send test">
              <label className="tx__label" htmlFor="tx-test">
                {isEmail ? "Send to this email address" : "Send to this mobile number"}
              </label>
              <input
                id="tx-test"
                className="tx__field"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
              <div className="tx__hint">
                {/* The campaign's own From address, because that is what CmdTestEmail
                    actually sends as -- it takes the from_address it is handed. The hint
                    used to name a separate test address and promise it was "never the
                    client's own", which was the opposite of what happens. */}
                {isEmail
                  ? testFrom
                    ? `Sends from ${testFrom}, exactly as the campaign will.`
                    : "Set a From address on the campaign before sending a test."
                  : "Sends from the school's registered number. Standard rates apply."}
              </div>
              {isEmail && draft.cta.trim() === "" && (
                <div className="tx__test-warn">! The button has no label yet.</div>
              )}
              <div className="tx__test-actions">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setTestOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={testTo.trim() === ""}
                  onClick={() => {
                    onSendTest(testTo.trim());
                    setTestOpen(false);
                  }}
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </span>

        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={readOnly || !dirty || ctaMissing}
          onClick={save}
        >
          {scope === "campaign" ? "Save for this campaign" : "Save to catalog"}
        </button>
      </div>
    </div>
  );
}

export default TouchEditor;
